import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NotificationBell from './NotificationBell';
import {
  HomeIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  UsersIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  BriefcaseIcon,
  MegaphoneIcon,
  DocumentPlusIcon,
  ArrowsRightLeftIcon,
  GlobeAltIcon,
  InboxIcon,
  TicketIcon,
  MagnifyingGlassIcon,
  PresentationChartLineIcon
} from '@heroicons/react/24/outline';

const Layout = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Check if user is sales role
  const isSales = user?.role === 'sales';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigation = [
    {
      section: 'Main',
      items: [
        { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
        { name: 'Clients', href: '/clients', icon: UserGroupIcon, adminOnly: true },
        { name: 'Service Calls', href: '/service-calls', icon: ClipboardDocumentListIcon },
        { name: 'Calendar', href: '/calendar', icon: CalendarDaysIcon },
        { name: 'Invoices', href: '/invoices', icon: DocumentTextIcon, adminOnly: true },
      ]
    },
    {
      section: 'Project Management',
      items: [
        { name: 'Proposals', href: '/proposals', icon: DocumentPlusIcon },
        { name: 'Change Orders', href: '/change-orders', icon: ArrowsRightLeftIcon },
      ]
    },
    {
      section: 'Sales',
      items: [
        { name: 'Lead Finder', href: '/lead-finder', icon: MagnifyingGlassIcon, adminOrSales: true },
        { name: 'Sales Dashboard', href: '/sales-dashboard', icon: PresentationChartLineIcon, adminOrSales: true },
        { name: 'Website Leads', href: '/website-leads', icon: InboxIcon, adminOnly: true },
      ]
    },
    {
      section: 'Website',
      items: [
        { name: 'Website CMS', href: '/website-cms', icon: GlobeAltIcon, adminOnly: true },
      ]
    },
    {
      section: 'Support',
      items: [
        { name: 'IT Support', href: '/it-support', icon: TicketIcon, adminOnly: true },
      ]
    },
    {
      section: 'Management',
      items: [
        { name: 'Users', href: '/users', icon: UsersIcon, adminOnly: true },
        { name: 'Reports', href: '/reports', icon: ChartBarIcon, adminOnly: true },
        { name: 'Human Resources', href: '/hr', icon: BriefcaseIcon },
        { name: 'Message Board', href: '/message-board', icon: MegaphoneIcon, adminOnly: true },
        { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
      ]
    }
  ];

  const getUserInitials = (name) => {
    return name
      ?.split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'U';
  };

  // Helper to check if item should be visible
  const isItemVisible = (item) => {
    if (item.adminOnly && !isAdmin) return false;
    if (item.adminOrSales && !isAdmin && !isSales) return false;
    return true;
  };

  return (
    <div className="app-container">
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <NavLink to="/" className="sidebar-logo">
            SVT Portal
          </NavLink>
          <div className="sidebar-subtitle">Service Management</div>
        </div>

        <nav className="sidebar-nav">
          {navigation.map((section) => {
            // Check if any items in this section are visible to the user
            const visibleItems = section.items.filter(item => isItemVisible(item));
            if (visibleItems.length === 0) return null;
            
            return (
              <div key={section.section} className="sidebar-section">
                <div className="sidebar-section-title">{section.section}</div>
                {section.items.map((item) => {
                  // Skip items not visible to this user
                  if (!isItemVisible(item)) return null;
                  
                  return (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      className={({ isActive }) =>
                        `sidebar-link ${isActive ? 'active' : ''}`
                      }
                      onClick={() => setSidebarOpen(false)}
                    >
                      <item.icon className="sidebar-icon" />
                      {item.name}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{user?.name}</div>
              <div className="sidebar-user-role">{user?.role}</div>
            </div>
          </div>
          <button onClick={handleLogout} className="sidebar-logout">
            <ArrowRightOnRectangleIcon className="sidebar-icon" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="main-content">
        {/* Header */}
        <header className="header">
          <div className="header-left">
            <button 
              className="mobile-menu-button"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              <Bars3Icon />
            </button>
          </div>
          
          <div className="header-right">
            <NotificationBell />
            
            <div className="header-user">
              <div className="header-user-avatar">
                {getUserInitials(user?.name)}
              </div>
              <span className="header-user-name">{user?.name}</span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="page-content">
          <Outlet />
        </main>
      </div>

      <style dangerouslySetInnerHTML={{__html: `
        .sidebar-icon {
          width: 20px;
          height: 20px;
        }
        
        .sidebar-user {
          margin-bottom: 12px;
        }
        
        .sidebar-user-info {
          padding: 8px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 6px;
        }
        
        .sidebar-user-name {
          font-size: 14px;
          font-weight: 600;
          color: white;
        }
        
        .sidebar-user-role {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.7);
          text-transform: capitalize;
        }
        
        .sidebar-logout {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          padding: 10px 8px;
          background: rgba(255, 255, 255, 0.1);
          border: none;
          border-radius: 6px;
          color: white;
          font-size: 14px;
          cursor: pointer;
          transition: background 0.2s;
        }
        
        .sidebar-logout:hover {
          background: rgba(255, 255, 255, 0.2);
        }
        
        .mobile-menu-button {
          display: none;
          padding: 8px;
          background: transparent;
          border: none;
          cursor: pointer;
          color: #4a5568;
        }
        
        .mobile-menu-button svg {
          width: 24px;
          height: 24px;
        }
        
        @media (max-width: 768px) {
          .mobile-menu-button {
            display: block;
          }
        }
      `}} />
    </div>
  );
};

export default Layout;
